import React from 'react'
import CheckOutlineIcon from 'transfix-icons/build/CheckOutlineIcon'
import { makeStyles, Typography } from 'transfix-ui/core'
import {
  IFacility,
  IFacilityIdentifier,
  IFacilityReviewStatuses,
  ILocation,
  IShipperFacility,
  IVendor,
  IVendorFacility,
  Maybe,
} from 'types/graphqlTypes'
import { getLastRanMessage } from './FacilitySelector/utils'

interface IShipperFacilityProp
  extends Pick<
    IShipperFacility,
    'id' | 'uuid' | 'totalShipmentsLastYear' | 'latestShipmentDelivery'
  > {
  identifiers: Pick<IFacilityIdentifier, 'id'>[]
}

interface IVendorFacilityProp extends Pick<IVendorFacility, 'id'> {
  vendor?: Maybe<Pick<IVendor, 'id' | 'name'>>
}

export interface IFacilityProp extends Pick<IFacility, 'id' | 'name' | 'reviewStatus' | 'uuid'> {
  location: Pick<ILocation, 'id' | 'address' | 'addressLine2' | 'city' | 'stateCode' | 'zipcode'>
  shipperFacilities?: Maybe<IShipperFacilityProp[]>
  vendorFacilities?: Maybe<IVendorFacilityProp[]>
}

export interface IFacilitySummaryProps {
  facility: IFacilityProp
}

const useFacilitySummaryStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    '& p': {
      padding: 0,
    },
  },
  name: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}))

export const FacilitySummary = ({ facility }: IFacilitySummaryProps) => {
  const classes = useFacilitySummaryStyles()

  const { location, name, reviewStatus, shipperFacilities, uuid } = facility || {}
  const { address, addressLine2, city, stateCode, zipcode } = location || {}
  const isPrimary = reviewStatus === IFacilityReviewStatuses.primary
  const [shipperFacility] = shipperFacilities || []
  const lastRanMessage = getLastRanMessage(shipperFacility)

  if (!uuid) return null

  return (
    <div className={classes.root}>
      <div>
        <div className={classes.name}>
          <Typography variant='body2'>
            <a href={`${__ROOT__}/facilities/${uuid}`} target='_blank' rel='noreferrer'>
              {name}
            </a>
          </Typography>
          {isPrimary && <CheckOutlineIcon color='green2' size='large' />}
        </div>
        <Typography variant='body2'>{address}</Typography>
        {addressLine2 && <Typography variant='body2'>{addressLine2}</Typography>}
        <Typography variant='body2'>
          {city}, {stateCode} {zipcode}
        </Typography>
      </div>
      <Typography color='textSecondary' variant='body2' component='span'>
        {lastRanMessage}
      </Typography>
    </div>
  )
}
